// frontend/src/pages/Vendors/VendorList.jsx
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getVendors, verifyVendor } from "../../api/vendor.api.js";
import { getEntities } from "../../api/entity.api.js";
import {
  PageHeader,
  Spinner,
  EmptyState,
  formatDate,
} from "../../components/ui";
import { CheckCircle, Search, ChevronRight, X, FileText } from "lucide-react";
import MultiSelect from "../../components/MultiSelect.jsx";

function FilterTag({ label, onRemove }) {
  return (
    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 border border-brand-200 text-xs text-brand-700">
      {label}
      <button onClick={onRemove} className="hover:text-brand-900 ml-0.5">
        <X size={11} />
      </button>
    </span>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function VendorList() {
  const { isMaster } = useAuth();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [filter, setFilter] = useState(searchParams.get("verified") ?? "all");
  const [groupIds, setGroupIds] = useState(() =>
    searchParams.getAll("groupId"),
  );
  const [entityIds, setEntityIds] = useState(() =>
    searchParams.getAll("entityId"),
  );

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (filter !== "all") params.set("verified", filter);
    groupIds.forEach((id) => params.append("groupId", id));
    entityIds.forEach((id) => params.append("entityId", id));
    setSearchParams(params, { replace: true });
  }, [search, filter, groupIds, entityIds]);

  const { data: vendors, isLoading } = useQuery({
    queryKey: ["vendors", filter, search, groupIds, entityIds],
    queryFn: () =>
      getVendors({
        ...(filter !== "all" && { isVerified: filter === "verified" }),
        ...(search && { search }),
        ...(groupIds.length && { groupId: groupIds }),
        ...(entityIds.length && { entityId: entityIds }),
      }),
  });

  const { data: entities } = useQuery({
    queryKey: ["entities-for-filter"],
    queryFn: () => getEntities(),
    enabled: isMaster,
  });

  const groups = entities
    ? [
        ...new Map(
          entities.filter((e) => e.group).map((e) => [e.group.id, e.group]),
        ).values(),
      ].sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const verify = useMutation({
    mutationFn: (id) => verifyVendor(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vendors"] }),
  });

  const activeFilterCount = groupIds.length + entityIds.length;
  const hasActiveFilters = !!(search || filter !== "all" || activeFilterCount);

  function clearFilters() {
    setSearch("");
    setFilter("all");
    setGroupIds([]);
    setEntityIds([]);
  }

  return (
    <div>
      <PageHeader title="Vendors" subtitle={`${vendors?.length ?? 0} total`} />

      {/* ── Filter bar ───────────────────────────────────────────────────── */}
      <div className="mb-5 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative max-w-xs flex-1">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              className="input pl-9 text-sm"
              placeholder="Search vendors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input w-36 text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600"
            >
              <X size={14} /> Clear all
            </button>
          )}
        </div>

        {isMaster && (
          <div className="flex flex-wrap gap-3 items-start">
            <MultiSelect
              label="Group"
              options={groups}
              selected={groupIds}
              onChange={setGroupIds}
            />
            <MultiSelect
              label="Entity"
              options={entities ?? []}
              selected={entityIds}
              onChange={setEntityIds}
            />
          </div>
        )}

        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2">
            {groupIds.map((gid) => (
              <FilterTag
                key={gid}
                label={`Group: ${groups.find((g) => g.id === gid)?.name ?? gid}`}
                onRemove={() => setGroupIds(groupIds.filter((v) => v !== gid))}
              />
            ))}
            {entityIds.map((eid) => (
              <FilterTag
                key={eid}
                label={`Entity: ${entities?.find((e) => e.id === eid)?.name ?? eid}`}
                onRemove={() =>
                  setEntityIds(entityIds.filter((v) => v !== eid))
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Vendor list ───────────────────────────────────────────────────── */}
      {isLoading ? (
        <Spinner />
      ) : !vendors?.length ? (
        <EmptyState
          title="No vendors found"
          description={
            hasActiveFilters ? "Try adjusting your filters." : undefined
          }
          action={
            hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="btn-secondary text-sm flex items-center gap-1.5"
              >
                <X size={13} /> Clear filters
              </button>
            )
          }
        />
      ) : (
        <div className="card divide-y divide-gray-100">
          {vendors.map((v) => {
            const activeCount = v._count?.agreements ?? 0;
            return (
              <div
                key={v.id}
                className="flex items-center justify-between px-6 py-4"
              >
                <Link
                  to={`/vendors/${v.id}`}
                  className="flex-1 min-w-0 hover:opacity-80"
                >
                  <p className="text-sm font-medium text-gray-900">
                    {v.companyName}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {v.picName} · {v.email} · Registered{" "}
                    {formatDate(v.registeredAt)}
                  </p>
                </Link>
                <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                  {/* Active agreement count */}
                  <span
                    className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                      activeCount > 0
                        ? "bg-green-50 text-green-700"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <FileText size={11} />
                    {activeCount} active
                  </span>
                  <span
                    className={`badge ${v.isVerified ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
                  >
                    {v.isVerified ? "Verified" : "Pending"}
                  </span>
                  {isMaster && !v.isVerified && (
                    <button
                      onClick={() => verify.mutate(v.id)}
                      disabled={verify.isPending}
                      className="btn-secondary flex items-center gap-1 text-xs py-1 px-2"
                    >
                      <CheckCircle size={13} /> Verify
                    </button>
                  )}
                  <Link to={`/vendors/${v.id}`}>
                    <ChevronRight size={16} className="text-gray-400" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
